import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTutorial } from '../contexts/TutorialContext';

// Global queue to manage concurrent thumbnail generation
const thumbnailQueue = {
  queue: [],
  processing: false,
  maxConcurrent: 2, // Limit concurrent thumbnail generation
  
  add(task) {
    this.queue.push(task);
    this.process();
  },
  
  async process() {
    if (this.processing || this.queue.length === 0) return;
    
    this.processing = true;
    const activeTasks = [];
    
    // Process up to maxConcurrent tasks
    while (this.queue.length > 0 && activeTasks.length < this.maxConcurrent) {
      const task = this.queue.shift();
      activeTasks.push(task());
    }
    
    if (activeTasks.length > 0) {
      await Promise.allSettled(activeTasks);
    }
    
    this.processing = false;
    
    // Continue processing if there are more tasks
    if (this.queue.length > 0) {
      setTimeout(() => this.process(), 100);
    }
  }
};

const TutorialContentItem = ({ content, hideCompletionStatus = false, hideRequiredStatus = false }) => {
  const { 
    userProgress, 
    selectContent, 
    updateContentProgress, 
    getContentUrl
  } = useTutorial();

  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [pdfThumbnail, setPdfThumbnail] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  
  const elementRef = useRef(null);
  const loadTimeoutRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.1
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, []);

  // Generate video thumbnail from video URL
  const generateVideoThumbnail = useCallback(async (videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.crossOrigin = 'anonymous';
      video.muted = true; // Ensure muted for autoplay policies
      video.currentTime = 1; // Capture frame at 1 second
      
      const cleanup = () => {
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('error', onError);
        video.src = '';
        video.load();
      };
      
      const onLoadedData = () => {
        try {
          canvas.width = 120;
          canvas.height = 90;
          ctx.drawImage(video, 0, 0, 120, 90);
          
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
          cleanup();
          resolve(thumbnailUrl);
        } catch (error) {
          cleanup();
          reject(error);
        }
      };
      
      const onError = () => {
        cleanup();
        reject(new Error('Failed to load video'));
      };
      
      video.addEventListener('loadeddata', onLoadedData);
      video.addEventListener('error', onError);
      video.src = videoUrl;
      video.load();
      
      // Timeout after 10 seconds
      setTimeout(() => {
        cleanup();
        reject(new Error('Video thumbnail generation timeout'));
      }, 10000);
    });
  }, []);

  // Generate PDF thumbnail from PDF URL
  const generatePdfThumbnail = useCallback(async (pdfUrl) => {
    try {
      // Use PDF.js to render first page
      const pdfjsLib = window.pdfjsLib;
      if (!pdfjsLib) {
        throw new Error('PDF.js not loaded');
      }

      const pdf = await pdfjsLib.getDocument(pdfUrl).promise;
      const page = await pdf.getPage(1);
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      // Set canvas size
      const viewport = page.getViewport({ scale: 0.5 });
      canvas.width = 120;
      canvas.height = 90;
      
      // Scale to fit thumbnail
      const scale = Math.min(120 / viewport.width, 90 / viewport.height);
      const scaledViewport = page.getViewport({ scale });
      
      await page.render({
        canvasContext: context,
        viewport: scaledViewport
      }).promise;
      
      return canvas.toDataURL('image/jpeg', 0.8);
    } catch (error) {
      console.error('Error generating PDF thumbnail:', error);
      throw error;
    }
  }, []);

  // Debounced load preview function
  const loadPreview = useCallback(async () => {
    if (!content || !content.itemId || !content.s3Key || hasStartedLoading) return;
    
    const contentStatus = userProgress?.getContentStatus(content.itemId) || {
      required: false,
      completed: false
    };
    
    // Skip loading preview for text content to avoid auto-download
    if (content.contentType === 'text') {
      return;
    }
    
    setHasStartedLoading(true);
    setPreviewLoading(true);
    setPreviewError(false);
    
    try {
      const url = await getContentUrl(content.s3Key, content.contentType);
      setPreviewUrl(url);
      
      // Queue thumbnail generation to avoid overwhelming the browser
      if (content.contentType === 'video') {
        thumbnailQueue.add(async () => {
          try {
            const thumbnail = await generateVideoThumbnail(url);
            setVideoThumbnail(thumbnail);
          } catch (error) {
            console.error('Error generating video thumbnail:', error);
            // Keep previewUrl for fallback
          }
        });
      }

      if (content.contentType === 'pdf') {
        thumbnailQueue.add(async () => {
          try {
            const thumbnail = await generatePdfThumbnail(url);
            setPdfThumbnail(thumbnail);
          } catch (error) {
            console.error('Error generating PDF thumbnail:', error);
            // Keep previewUrl for fallback
          }
        });
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewError(true);
    } finally {
      setPreviewLoading(false);
    }
  }, [content, userProgress, getContentUrl, hasStartedLoading, generateVideoThumbnail, generatePdfThumbnail]);

  // Load preview when visible with debounce
  useEffect(() => {
    if (isVisible && !hasStartedLoading) {
      // Add random delay to spread out the load
      const delay = Math.random() * 500 + 200; // 200-700ms random delay
      loadTimeoutRef.current = setTimeout(() => {
        loadPreview();
      }, delay);
    }
    
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [isVisible, loadPreview, hasStartedLoading]);

  if (!content || !content.itemId) return null;

  const contentStatus = userProgress?.getContentStatus(content.itemId) || {
    required: false,
    completed: false
  };
  
  const isRequired = contentStatus.required;
  const isCompleted = contentStatus.completed;

  // Get content thumbnail/preview
  const getContentThumbnail = () => {
    if (previewLoading) {
      return (
        <Box
          sx={{
            width: '120px',
            height: '90px',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            flexShrink: 0,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      );
    }
    
    switch (content.contentType) {
      case 'video':
        return (
          <Box
            sx={{
              width: '120px',
              height: '90px',
              borderRadius: 1,
              flexShrink: 0,
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: '#000',
            }}
          >
            {(previewUrl || videoThumbnail) && !previewError ? (
              <>
                {/* Video thumbnail using generated image */}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: videoThumbnail 
                      ? `url(${videoThumbnail})` 
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* Play button overlay */}
                  <Box
                    sx={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      }
                    }}
                  >
                    <PlayArrowIcon sx={{ color: 'white', fontSize: '1.5rem', ml: '2px' }} />
                  </Box>
                </Box>
                
                {/* Video duration badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.7rem',
                  }}
                >
                  VIDEO
                </Box>
              </>
            ) : (
              // Fallback for video
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlayArrowIcon sx={{ color: '#333', fontSize: '1.5rem', ml: '2px' }} />
                </Box>
              </Box>
            )}
          </Box>
        );

      case 'pdf':
        return (
          <Box
            sx={{
              width: '120px',
              height: '90px',
              borderRadius: 1,
              flexShrink: 0,
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
            }}
          >
            {(previewUrl || pdfThumbnail) && !previewError ? (
              <>
                {/* PDF thumbnail using generated image */}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: pdfThumbnail 
                      ? `url(${pdfThumbnail})` 
                      : 'linear-gradient(to bottom, #fff 0%, #f8f8f8 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* Show PDF icon only if no thumbnail */}
                  {!pdfThumbnail && (
                    <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: '2.5rem' }} />
                  )}
                </Box>
                
                {/* PDF overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    backgroundColor: 'rgba(211, 47, 47, 0.9)',
                    color: 'white',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                >
                  PDF
                </Box>
              </>
            ) : (
              // Fallback for PDF
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundImage: 'linear-gradient(to bottom, #fff 0%, #f8f8f8 100%)',
                }}
              >
                <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: '2.5rem', mb: 0.5 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                >
                  PDF
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 'text':
        return (
          <Box
            sx={{
              width: '120px',
              height: '90px',
              borderRadius: 1,
              flexShrink: 0,
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
            }}
          >
            {/* Always show text icon for text content - no preview to avoid auto-download */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: 'linear-gradient(to bottom, #fff 0%, #f9f9f9 100%)',
              }}
            >
              <TextSnippetIcon sx={{ color: '#ff9800', fontSize: '2.5rem', mb: 0.5 }} />
              <Typography
                variant="caption"
                sx={{
                  color: '#666',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                }}
              >
                TEXT
              </Typography>
            </Box>
          </Box>
        );

      default:
        return (
          <Box
            sx={{
              width: '120px',
              height: '90px',
              backgroundColor: '#e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              flexShrink: 0,
            }}
          >
            <PlayArrowIcon sx={{ color: '#666', fontSize: '2rem' }} />
          </Box>
        );
    }
  };

  // Handle content click
  const handleContentClick = () => {
    selectContent(content);
  };

  // Handle completion toggle
  const handleCompletionToggle = async (event) => {
    event.stopPropagation();
    try {
      await updateContentProgress(content.itemId, !isCompleted);
    } catch (error) {
      console.error('Error updating content progress:', error);
    }
  };

  return (
    <Box ref={elementRef} sx={{ mb: 0 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 2, 
          p: 2,
          border: 'none', // Remove border between content items
          borderRadius: 0, // Remove border radius for seamless connection
          backgroundColor: '#f6f6f6', // Updated background color
          cursor: 'pointer',
          marginTop: '0', // Remove negative margin since no borders
          '&:hover': {
            backgroundColor: '#ededed', // Updated hover color to match f6f6f6 background
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        }}
        onClick={handleContentClick}
      >
        {/* Content thumbnail/icon */}
        {getContentThumbnail()}

        {/* Content details */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Typography 
              variant="subtitle1"
              sx={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#333',
                flex: 1,
              }}
            >
              {content.title}
            </Typography>
            
            {/* Status indicators */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              {/* Required indicator */}
              {!hideRequiredStatus && isRequired && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#d32f2f', 
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                >
                  Required
                </Typography>
              )}
              
              {/* Completion indicator - only show if not in admin mode */}
              {!hideCompletionStatus && (
                isCompleted ? (
                <CheckCircleIcon 
                  sx={{ 
                    color: '#388e3c', 
                    fontSize: '1.2rem',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#2e7d32'
                    }
                  }}
                  onClick={handleCompletionToggle}
                />
              ) : (
                <CheckCircleIcon 
                  sx={{ 
                    color: '#e0e0e0', 
                    fontSize: '1.2rem',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#388e3c'
                    }
                  }}
                  onClick={handleCompletionToggle}
              />
                )
              )}
            </Box>
          </Box>

          {/* Content description */}
          {content.description && (
            <Typography 
              variant="body2"
              sx={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: '#666',
              }}
            >
              {content.description}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TutorialContentItem; 